import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { PATH } from '../../../../constants/path';
import { BRAND_NAME, HAS_INSTANT_DISCOUNT, USE_PARITY } from '../../../../constants';

import { registerCart } from '../../../../services/ebon/actions';

import { getI18nByApp } from '../../../../services/i18n/selector';

import gtmUtils from '../../../../utils/gtmUtils';
import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';
import { convertPartnerNameForUrl } from '../../../../services/partner/utils';
import { formatAmountCurrency, formatAmountParity, getParityTotal } from '../../../../utils';

import BasketRowItem from '../BasketRowItem';
import ButtonComponent from '../../../button/ButtonComponent';

import { SelectorRightContainer } from './EbonSelectorRightContainerStyle';

const EbonSelectorRightContainer = ({
    partner,
    cart,
    handleItemChange,
    getCartTotal,
    isBasketLimitReached,
    loginRequired,
    showLoginModal,
    i18n,
    i18nPage,
    ebons,
    history,
    registerCart
}) => {
    const [CGVAccepted, setCGVAccepted] = useState(false);
    const { ebonDiscount = {} } = ebons;

    const discount = USE_PARITY ? ebonDiscount.parity : ebonDiscount.cashback;
    const fixed = discount.fixed.value;
    const percent = discount.percent.value;
    const amountCashback = fixed > 0 ? fixed : percent;

    const getLabelText = () => {
        if (HAS_INSTANT_DISCOUNT) {
            return getI18nByApp(i18n, 'page.MERCHANTCARD.EBON_SELECTOR.INSTANT_DISCOUNT');
        } else {
            return convertHtmlToReactDomWithParams(
                getI18nByApp(i18n, 'page.MERCHANTCARD.EBON_SELECTOR.DISCOUNT'),
                BRAND_NAME
            );
        }
    };

    const cartValidation = () => {
        gtmUtils.event('begin_checkout', { cart, partner: { name: partner.name, id: partner.id } });

        registerCart(cart);
        const validationPath = `${PATH.EBON_VALIDATION_PATH}/${convertPartnerNameForUrl(
            partner.name
        )}-${partner.id}`;
        history.push(validationPath);
    };

    const getCashbackAmount = cart => {
        const cartTotal = getCartTotal(cart);
        return (cartTotal * (100 - amountCashback)) / 100;
    };

    const doCartTotalCashbackAmount = cart => {
        const cartTotal = getCartTotal(cart);
        return Number((cartTotal - getCashbackAmount(cart)).toFixed(2));
    };

    const doCartTotalCashbackAmountParity = cart => {
        const cartTotal = getCartTotal(cart);
        const totalCashbackAmount = getParityTotal(cartTotal, amountCashback);
        const normalizedCashBackNumber = Number(totalCashbackAmount.toFixed(2));
        return normalizedCashBackNumber;
    };

    const doCartTotalTtc = cart => {
        const cartTotal = getCartTotal(cart);
        const totalCashbackAmount = HAS_INSTANT_DISCOUNT ? getCashbackAmount(cart) : cartTotal;
        const normalizedCashBackNumber = Number(totalCashbackAmount.toFixed(2));
        return normalizedCashBackNumber;
    };

    return (
        <SelectorRightContainer>
            <p className="basket">
                <span>{i18nPage.BASKET}</span>
            </p>
            <div className="cart-container">
                {cart.length < 1 ? (
                    <p>{i18nPage.EMPTY_BASKET}</p>
                ) : (
                    <React.Fragment>
                        {cart
                            .sort((a, b) => a.facialValue - b.facialValue)
                            .map((e, i) => {
                                const amount = (e.facialValue || 0) * e.quantityQuery || 0;
                                return (
                                    <BasketRowItem
                                        text={`${i18nPage.BON_ACHAT} - ${partner.name}`}
                                        ebon={e}
                                        amount={amount}
                                        key={i}
                                        handleItemChange={handleItemChange}
                                        isBasketLimitReached={isBasketLimitReached()}
                                    />
                                );
                            })}
                    </React.Fragment>
                )}
            </div>

            <div>
                <div className="resum-container">
                    <div className="row-text">
                        <p>{i18nPage.DELIVERY}</p>
                        <p>{i18nPage.FREE}</p>
                    </div>

                    <div className="row-text colored">
                        <p>{getLabelText()}</p>
                        <span className="amount">
                            {USE_PARITY
                                ? formatAmountParity(
                                      doCartTotalCashbackAmountParity(cart, amountCashback)
                                  )
                                : formatAmountCurrency(
                                      doCartTotalCashbackAmount(cart, amountCashback)
                                  )}
                        </span>
                    </div>

                    <div className="row-text">
                        <p>{i18nPage.TOTAL_TTC}</p>
                        <span className="amount">{formatAmountCurrency(doCartTotalTtc(cart))}</span>
                    </div>
                </div>

                <div className="cgv-container">
                    <label className="label">
                        <input
                            type="checkbox"
                            value={CGVAccepted}
                            onChange={() => setCGVAccepted(!CGVAccepted)}
                        />
                        <span className="checkmark"></span>
                    </label>
                    <p>{convertHtmlToReactDomWithParams(i18nPage.CGV_TEXT, PATH.TERMS_OF_SALES)}</p>
                </div>

                <div className="button-add validateCart">
                    {!loginRequired ? (
                        <ButtonComponent
                            disabled={!CGVAccepted || cart.length <= 0}
                            onClick={() => cartValidation()}
                            color="main"
                            text={i18nPage.VALIDATE_BASKET}
                        />
                    ) : (
                        <ButtonComponent
                            disabled={!CGVAccepted || cart.length <= 0}
                            onClick={e => showLoginModal(e)}
                            color="main"
                            text={i18nPage.LOGIN_TO_VALIDATE_BASKET}
                        />
                    )}
                </div>
            </div>
        </SelectorRightContainer>
    );
};

EbonSelectorRightContainer.propTypes = {
    partner: PropTypes.object.isRequired,
    cart: PropTypes.array.isRequired,
    handleItemChange: PropTypes.func.isRequired,
    getCartTotal: PropTypes.func.isRequired,
    isBasketLimitReached: PropTypes.func,
    loginRequired: PropTypes.bool.isRequired,
    showLoginModal: PropTypes.func.isRequired
};

const mapStateToProps = state => {
    return {
        i18n: state.i18n,
        i18nPage: state.i18n.page.MERCHANTCARD.EBON_SELECTOR,
        ebons: state.ebon.ebons
    };
};

const mapDispatchToProps = {
    registerCart
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EbonSelectorRightContainer));



// WEBPACK FOOTER //
// ./src/components/pages/merchantcard/ebonselector/EbonSelectorRightContainer.js